import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  FileText,
  Gavel,
  Home,
  Sparkles,
  User2,
  Video,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";
import { listMyCases, type CaseSummary } from "@/lib/api";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Palette de commandes universelle (raccourci ⌘K / Ctrl+K depuis
// l'ArbiterShell). En P1 : navigation rapide entre pages app + chaque
// dossier (vers detail / audience / decisions).
//
// Hors-scope (a venir) : actions globales ("Lancer ancrage", "Signer
// derniere decision", "Activer Focus Mode"), recherche RAG inline.
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { session } = useAuth();
  const [, setLocation] = useLocation();
  const [cases, setCases] = useState<CaseSummary[]>([]);

  // Refresh la liste a chaque ouverture pour ne pas servir un cache
  // perime apres ajout d'un dossier par un admin.
  useEffect(() => {
    if (!session || !open) return;
    let cancelled = false;
    listMyCases()
      .then((data) => {
        if (!cancelled) setCases(data);
      })
      .catch(() => {
        if (!cancelled) setCases([]);
      });
    return () => {
      cancelled = true;
    };
  }, [session, open]);

  const navigate = (path: string) => {
    setLocation(path);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Rechercher un dossier, une commande…" />
      <CommandList>
        <CommandEmpty>Aucun résultat.</CommandEmpty>

        <CommandGroup heading="Navigation">
          <CommandItem
            value="dashboard tableau de bord accueil"
            onSelect={() => navigate("/dashboard")}
          >
            <Home size={14} />
            <span>Tableau de bord</span>
          </CommandItem>
          <CommandItem
            value="recherche ia rag juridique"
            onSelect={() => navigate("/recherche")}
          >
            <Sparkles size={14} />
            <span>Recherche juridique IA</span>
          </CommandItem>
          <CommandItem
            value="profil compte parametres"
            onSelect={() => navigate("/profil")}
          >
            <User2 size={14} />
            <span>Mon profil</span>
          </CommandItem>
        </CommandGroup>

        {cases.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Mes dossiers">
              {cases.map((c) => (
                <CommandItem
                  key={`case-${c.id}`}
                  value={`dossier ${c.numero_gsf} ${c.objet_litige}`}
                  onSelect={() => navigate(`/cases/${c.id}`)}
                >
                  <FileText size={14} />
                  <span
                    className="font-mono text-xs"
                    style={{ color: "var(--proof-gold)" }}
                  >
                    {c.numero_gsf}
                  </span>
                  <span className="text-muted-foreground truncate ml-2">
                    — {c.objet_litige}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />
            <CommandGroup heading="Audiences">
              {cases.map((c) => (
                <CommandItem
                  key={`audience-${c.id}`}
                  value={`audience visio ${c.numero_gsf}`}
                  onSelect={() => navigate(`/audiences/${c.id}`)}
                >
                  <Video size={14} />
                  <span>Audience {c.numero_gsf}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />
            <CommandGroup heading="Décisions arbitrales">
              {cases.map((c) => (
                <CommandItem
                  key={`decisions-${c.id}`}
                  value={`decision sentence ordonnance ${c.numero_gsf}`}
                  onSelect={() => navigate(`/cases/${c.id}/decisions`)}
                >
                  <Gavel size={14} />
                  <span>Décisions {c.numero_gsf}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
